import {
  BodyText,
  Box,
  Button,
  DescriptionList,
  DescriptionListItem,
  Flex,
  Strong,
} from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import {
  type BroadbandAppointment,
  BroadbandAppointmentSlot,
} from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  signupApplicationEntity,
  useTrackSignupSuggestedBroadbandAppointmentCardViewedOnce,
} from '@tracking/trackingEvents';

const slotLabel = (slot: BroadbandAppointmentSlot) =>
  slot === BroadbandAppointmentSlot.Am ? 'Morning' : 'Afternoon';

export interface InstallationAppointmentCardProps extends Pick<
  BroadbandAppointment,
  'date' | 'slot'
> {
  onClickChangeAppointment?: () => void;
}

export const InstallationAppointmentCard = ({
  date,
  slot,
  onClickChangeAppointment,
}: InstallationAppointmentCardProps) => {
  const { applicationTrackingContext } = useTrackingContext();
  const trackSuggestedAppointmentCardViewed =
    useTrackSignupSuggestedBroadbandAppointmentCardViewedOnce();
  const formattedDate = dayjs(date).format('dddd, D MMMM YYYY');
  const timeRange = slot
    ? slot === BroadbandAppointmentSlot.Am
      ? '8am - 1pm'
      : '1pm - 6pm'
    : null;

  useEffect(() => {
    const contexts = applicationTrackingContext
      ? [signupApplicationEntity(applicationTrackingContext)]
      : [];

    trackSuggestedAppointmentCardViewed(undefined, { contexts });
  }, [trackSuggestedAppointmentCardViewed, applicationTrackingContext]);

  return (
    <Box
      borderStyle="solid"
      borderColor="subtle"
      borderWidth="1"
      borderRadius="md"
      padding="200"
      width="100%"
    >
      <Flex direction="column" gap="100">
        <Flex direction="column" gap="100">
          <Strong>Installation appointment</Strong>
          <BodyText color="secondary">
            This is the next available date for an engineer to set up your
            broadband. This date may change, but we'll always confirm the
            appointment with you.
          </BodyText>
        </Flex>
        <DescriptionList>
          <DescriptionListItem heading="Date" description={formattedDate} />
          <DescriptionListItem
            heading="Time"
            description={slot ? `${slotLabel(slot)} (${timeRange})` : ''}
          />
        </DescriptionList>
      </Flex>
      {onClickChangeAppointment && (
        <Box marginTop="200">
          <Button
            size="md"
            type="button"
            variant="outline"
            colorScheme="functional"
            onClick={onClickChangeAppointment}
          >
            Change my appointment
          </Button>
        </Box>
      )}
    </Box>
  );
};
